/**
 * GetAppPage.js — Always-on "Get the App" landing page.
 * Route: /get-app
 *
 * Purpose: Shannon meets someone in person and says "text yourself this link:
 * moovinforward.org/get-app". They open it, see a clean install card, tap
 * one button, and the PWA installs. No 30-second engagement wait, no dismiss
 * cooldown — this page clears the dismiss flag on mount so InstallPrompt
 * fires reliably.
 */
import React, { useEffect, useState } from "react";

const NAVY = "#020A1F"; const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";
const shell = { minHeight: "100dvh", background: NAVY, color: INK, padding: "clamp(20px,4vw,40px)", fontFamily: "'Inter', system-ui, sans-serif", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" };
const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CYAN}44`, borderRadius: 16, padding: "clamp(20px,3vw,32px)", maxWidth: 480, width: "100%", textAlign: "center" };

const isIOS = () => typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () => (typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
   window.navigator.standalone === true));

export default function GetAppPage() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [status, setStatus] = useState(null);
  const [ios, setIos] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Wipe the 30-day dismiss cooldown so InstallPrompt fires fresh.
    try {
      localStorage.removeItem("mf:install-prompt:dismissed_at:v2");
      localStorage.removeItem("mf:install-prompt:dismissed_at");
    } catch (_) { /* ignore */ }

    if (isStandalone()) { setInstalled(true); return; }
    if (isIOS()) { setIos(true); return; }

    const onBeforeInstall = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) {
      setStatus("Chrome hasn't sent the install signal yet. Interact with the page for ~15 seconds, then try again — or refresh.");
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    else setStatus("You dismissed the prompt. Tap Install again anytime.");
    setDeferredPrompt(null);
  };

  if (installed) return (
    <div style={shell}>
      <div style={card} data-testid="get-app-installed">
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>MOOVIN FORWARD LLC</div>
        <h1 style={{ margin: 0, fontSize: 28 }}>✓ App Installed</h1>
        <p style={{ color: CHROME, fontSize: 14, marginTop: 12 }}>
          You're all set. Open the Moovin Forward app from your home screen anytime.
        </p>
      </div>
    </div>
  );

  return (
    <div style={shell}>
      <div style={card} data-testid="get-app-page">
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 8 }}>
          MOOVIN FORWARD LLC · GET THE APP
        </div>
        <h1 style={{ margin: 0, fontSize: "clamp(24px, 4vw, 32px)" }}>
          One-tap install
        </h1>
        <p style={{ color: CHROME, fontSize: 14, marginTop: 12, lineHeight: 1.5 }}>
          Add the Moovin Forward digital platform to your home screen. Works offline,
          launches full-screen, gets push updates when new 1099 jobs land in your zone.
        </p>

        {!ios && (
          <button onClick={install} data-testid="get-app-install-btn" style={{
            marginTop: 20, width: "100%", padding: "16px 24px",
            background: CYAN, color: NAVY, border: 0, borderRadius: 10,
            fontWeight: 800, fontSize: 15, letterSpacing: "0.05em",
            textTransform: "uppercase", cursor: "pointer",
          }}>
            📱 Install Moovin Forward
          </button>
        )}

        {ios && (
          <div style={{ marginTop: 20, padding: 16, background: "rgba(0,207,207,0.08)", borderRadius: 10, textAlign: "left" }} data-testid="get-app-ios-help">
            <div style={{ fontSize: 12, color: CYAN, letterSpacing: "0.1em", marginBottom: 8 }}>iOS · SAFARI · 3-TAP INSTALL</div>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.7 }}>
              <li>Tap the <strong>Share</strong> icon at the bottom of Safari</li>
              <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
              <li>Tap <strong>Add</strong> — the icon appears on your home screen</li>
            </ol>
          </div>
        )}

        {status && (
          <div style={{ marginTop: 14, fontSize: 12, color: "#f5a942" }} data-testid="get-app-status">{status}</div>
        )}

        <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${CHROME}22`, fontSize: 11, color: CHROME }}>
          Share this page: <strong style={{ color: INK }}>moovinforward.org/get-app</strong>
        </div>
      </div>
    </div>
  );
}
